/**
 * Centralized business constants for AETHER.
 * All business-specific strings, statuses, and configuration values belong here.
 */
import type { UnscheduledEvent } from './types';

export const EVENT_STATUS = {
  PENDING: 'PENDING',
  IN_WORK: 'IN_WORK',
  COMPLETED: 'COMPLETED',
  DELETED: 'DELETED',
} as const;

export type EventStatus = (typeof EVENT_STATUS)[keyof typeof EVENT_STATUS];

/**
 * Note statements that indicate an inbound Triage event has been completed.
 * The "green-family" forms dominate the real ATSG feed (a standalone "A/C
 * GREEN"/"ACFT green" is by far the most common final-notification wording),
 * so both the bare and the "is"/"is a" variants are matched.
 */
export const TRIAGE_COMPLETION_PHRASES: readonly string[] = [
  // Green family (dominant in the ATI/ATN update feed; the final line is often
  // "MX complete. Paperwork received. Acft is GREEN.").
  'A/C green',
  'AC green',
  'ACFT green',
  'Aircraft green',
  'A/C is green',
  'AC is green',
  'ACFT is green',
  'Aircraft is green',
  // "Go" variants.
  'A/C is go',
  'A/C is a go',
  'Aircraft is go',
  'Aircraft is a go',
  // Return-to-service variants. "Released into/to service" (ASL AMOS, Hawaiian
  // /Alaska MOC), past-tense "returned to service" (SCX: "Aircraft returned to
  // service at 1800z."), and the common "back in/to service" phrasing.
  'Released into service',
  'Released to service',
  'Aircraft return to service',
  'returned to service',
  'back in service',
  'back to service',
  // Serviceable / cleared-for-service variants (Hawaiian/Alaska MOC completion
  // bullets, e.g. "A/C OK FOR SVC.").
  'Aircraft is serviceable',
  'OK for service',
  'OK for svc',
  // Paperwork / log-page received. Carriers send the closeout log page(s) or
  // paperwork once maintenance is complete, so its receipt marks completion
  // (e.g. ATSG's "MX complete. Paperwork received.").
  'paperwork received',
  'log page received',
  'log pages received',
  'logpage received',
  'logpages received',
];

/**
 * UI-only sentinel for the Status dropdown's "Move Event" action. It is never
 * persisted as a real status: selecting it reveals the move-target record-type
 * picker, and on Update the source record is saved as COMPLETED with its
 * `moveStatus` set to the chosen target type while a pre-populated Create Event
 * window opens for the destination record.
 */
export const MOVE_EVENT_OPTION = 'MOVE_EVENT';

/**
 * UI-only placeholder shown in the Delay (Minutes) field once the Delay IROP is
 * checked but no duration has been entered yet. It signals an ongoing delay of
 * unknown length; it is never persisted (the underlying `delayTimeMinutes`
 * stays 0 until the user types the actual minutes).
 */
export const ONGOING_DELAY_LABEL = 'ONGO';

/**
 * Confidence sentinel written to an event when a previously-existing ERTS is
 * removed. Recorded in ERTS history, but treated as "no ERTS" on the homepage
 * confidence column so a removed ERTS reads the same as one never entered.
 */
export const ERTS_REMOVED_CONFIDENCE = 'Removed ERTS';

/**
 * Change reason recorded for the first ERTS logged on a record. Its confidence
 * remains empty until the user selects the required confidence value.
 */
export const ERTS_INITIAL_CHANGE_REASON = 'Initial ERTS';

/**
 * Change reason recorded when an event is completed, capturing the final ERTS.
 * Its confidence is auto-populated as {@link ERTS_FINAL_CONFIDENCE} and is never
 * offered as a manually selectable confidence option.
 */
export const ERTS_FINAL_CHANGE_REASON = 'Final ERTS';

/**
 * Confidence auto-populated for the final ERTS when an event is completed.
 */
export const ERTS_FINAL_CONFIDENCE = 'Final';

export const CARRIERS = {
  ATN: 'ATN',
  SCX: 'SCX',
  ASA: 'ASA',
} as const;

export type Carrier = (typeof CARRIERS)[keyof typeof CARRIERS];

export const STATIONS = [
  'CVG',
  'ILN',
  'PHX',
  'IAH',
  'BWI',
  'SEA',
  'ONT',
  'LAS',
  'MCI',
  'LIH',
  'AUS',
  'BOI',
  'SMF',
  'LAL',
  'JFK',
] as const;

export type Station = (typeof STATIONS)[number];

export const IROP_FLAGS = {
  RTB: 'returnToBlock',
  ATO: 'abortedTakeOff',
  ATB: 'airTurnBack',
  DIV: 'diversion',
  CXL: 'cancellation',
  DLY: 'delay',
  DMG: 'damage',
} as const;

/**
 * A single IROP display rule: the event flag that turns the IROP on, and the
 * label shown in the homepage IROP column.
 */
export interface IropDisplayRule {
  readonly flag: keyof UnscheduledEvent;
  readonly label: string;
}

/**
 * IROP display configuration for the homepage IROP column, in display order.
 * Each active IROP renders on its own centered line. The first seven entries are
 * the IROP checkboxes (order and labels are business-defined); Long Term (LT) and
 * Weekend Sit (WS) are Planned-record flags surfaced in the same column and are
 * appended last.
 */
export const IROP_DISPLAY: readonly IropDisplayRule[] = [
  { flag: 'delay', label: 'DELAY' },
  { flag: 'cancellation', label: 'CANCEL' },
  { flag: 'damage', label: 'DAMAGE' },
  { flag: 'diversion', label: 'DIVERT' },
  { flag: 'returnToBlock', label: 'RTB' },
  { flag: 'abortedTakeOff', label: 'ATO' },
  { flag: 'airTurnBack', label: 'ATB' },
  { flag: 'longTerm', label: 'LT' },
  { flag: 'weekendSit', label: 'WS' },
];

/** The IROP display flag whose label is rendered in purple when its SkyDent Damage Location is incomplete. */
export const DAMAGE_IROP_FLAG: keyof UnscheduledEvent = 'damage';

/** Selectable History-window sizes (in days) offered in the History tab dropdown. */
export const HISTORY_WINDOW_OPTIONS = [30, 60, 90, 120, 150, 180] as const;

/** Default History-window size (in days) shown when the History tab first loads. */
export const DEFAULT_HISTORY_WINDOW_DAYS = 90;

/** Completed-event lookback used when determining whether a defect has related history. */
export const DEFECT_HISTORY_LOOKBACK_DAYS = 180;

export const ERTS_CONFIDENCE = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
} as const;

export const ERTS_CHANGE_REASONS = [
  'Parts ETA Change',
  'Additional Parts Required',
  'Additional Mx Findings',
  'Additional Mx Time Required',
  'Tooling',
  'Equipment',
  'Dark Hours',
  'ERTS Correction from Carrier',
  'Final ERTS',
  'Other',
] as const;

/**
 * Change-reason options logged when a Deferred record's Due Date is changed
 * after its initial value — mirrors the ERTS change-reason ceremony so each Due
 * Date revision is tracked with why it changed.
 */
export const DUE_DATE_CHANGE_REASONS = [
  'Extension Granted',
  'CAT Recategorization',
  'Correction',
  'Other',
] as const;

export const BARRIER_TYPES = [
  'Parts',
  'Tooling',
  'Manpower',
  'Equipment',
  'Engineering',
  'Dark Hours',
  'Other',
] as const;

/**
 * Display abbreviations for each barrier type, keyed by the BARRIER_TYPES value.
 * Used to label the per-type total open-time summary in the event modals.
 */
export const BARRIER_TYPE_ABBREVIATIONS: Record<string, string> = {
  Parts: 'PT',
  Tooling: 'TL',
  Manpower: 'MP',
  Equipment: 'EQ',
  Engineering: 'EN',
  'Dark Hours': 'DH',
  Other: 'OT',
};

export const RECORD_TYPES = ['Unscheduled', 'Planned', 'Deferred', 'Scheduled'] as const;

export const FCP_CODES = [
  'Carrier Comms Issues',
  'Decision to Recover',
  'Documentation Delay',
  'External Requests',
  'Lesson Learned',
  'Long Ground Time Finding',
  'MX Practice Improvement',
  'Parts',
  'Short Ground Time',
  'Timeline to DMI',
  'Wins',
] as const;

export type FcpCode = (typeof FCP_CODES)[number];

/**
 * Deferred CAT severity options, including the non-MEL tracking category (TRK).
 * Rendered as a single-select group in the Deferred section.
 */
export const CAT_OPTIONS = ['A', 'B', 'C', 'D', 'TRK'] as const;

export type CatOption = (typeof CAT_OPTIONS)[number];

/**
 * Number of days added to Time Declared to auto-populate a Deferred record's Due
 * Date, keyed by CAT. CATs absent from this map (A, TRK) have no automatic Due
 * Date — it must be entered manually and is required to save.
 */
export const CAT_DUE_DATE_OFFSET_DAYS: Record<string, number> = {
  B: 3,
  C: 10,
  D: 120,
  TRK: 14,
};

/** CATs whose Due Date must be entered manually and is required to save. */
export const CAT_MANUAL_DUE_DATE: readonly string[] = ['A'];

/**
 * How many days before the Due Date the Deferred Due Date begins flashing amber,
 * keyed by CAT. On the Due Date and every day after it flashes red instead.
 */
export const CAT_DUE_DATE_AMBER_LEAD_DAYS: Record<string, number> = {
  A: 2,
  B: 2,
  C: 3,
  D: 7,
  TRK: 3,
};

export const API_BASE = '/api';

/**
 * Email-triage feature flag. OFF by default — the Triage tab and section are
 * hidden and unreachable until this is enabled. The feature is built but parked
 * pending the inbound-email routing (SES recipient + mail forwarding) coordination;
 * see backend/TRIAGE-RUNBOOK.md. Flip via REACT_APP_TRIAGE_ENABLED=true (build
 * time) to re-enable once ingestion is in place.
 */
export const TRIAGE_ENABLED: boolean =
  (process.env.REACT_APP_TRIAGE_ENABLED || '').toLowerCase() === 'true';

/** Polling interval for pending parsed-email Triage items. */
export const TRIAGE_REFRESH_INTERVAL_MS = 15_000;

/**
 * SkyDent is a separate application in the same AWS account used to record
 * aircraft damage. When a Damage IROP is flagged with a damage code, AETHER
 * surfaces a link to SkyDent. Overridable per environment via build-time env var.
 */
export const SKYDENT_URL =
  process.env.REACT_APP_SKYDENT_URL || 'https://skydent-mxo.air.amazon.dev';

/** SkyDent's favicon, shown as the affordance that opens the SkyDent app. */
export const SKYDENT_FAVICON_URL = `${SKYDENT_URL}/favicon.ico`;

/**
 * LOFT is a separate application in the same AWS account for landing gear
 * tracking. The Landing Gear nav tab opens it in a new browser tab.
 * Overridable per environment via build-time env var.
 */
export const LANDING_GEAR_URL =
  process.env.REACT_APP_LANDING_GEAR_URL || 'https://loftgamma-mxo.air.amazon.dev/';

export const REFRESH_INTERVAL_MS = 900_000; // 15 minutes

/**
 * Polling interval for refreshing the active event list on the homepage while
 * the tab is visible. Per docs/ARCHITECTURE.md the read path is a per-browser
 * ~2s poll of GET /events?status=active, giving a ~0–2s propagation window for
 * changes made by other users. Polling is tab-visibility gated (see App.tsx):
 * backgrounded tabs stop polling, and a visibility/focus refetch catches up on
 * return. The failed-fetch null-guard means a missed poll never blanks the table.
 */
export const ACTIVE_EVENTS_REFRESH_INTERVAL_MS = 2_000; // 2 seconds

/**
 * Heartbeat cadence for refreshing the current user's edit lock while the Edit
 * modal is open. Must be comfortably under the backend lock TTL (~120s) so a
 * lock never lapses mid-edit. Released on modal close/save.
 */
export const EDIT_LOCK_HEARTBEAT_INTERVAL_MS = 60_000; // 60 seconds

/**
 * Polling interval for refreshing ML-projected ERTS values on the homepage.
 * Matches the prediction pipeline's 5-minute inference cadence so the Projected
 * ERTS column reflects new projections shortly after they are computed.
 */
export const PROJECTED_ERTS_REFRESH_INTERVAL_MS = 300_000; // 5 minutes

/**
 * Threshold (in minutes) within which an upcoming Next Advise or ERTS/RTS time
 * is considered imminent and highlighted yellow. Past times are highlighted red.
 */
export const TIME_ALERT_WARNING_THRESHOLD_MINUTES = 30;
